import React from "react";
import { FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { Clock, Pencil } from "lucide-react";

import { CardWithTitle } from "components/helpers/CardWithTitle/CardWithTitle.jsx";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { useSavePatientBaseline } from "features/patients/hooks/useSavePatientBaseline";
import { formatDate } from "utils/dateFormatter";
import { onInvalidSubmit } from "utils/reportFormErrors";

import s from "./BaselineDetails.module.css";
import {
  FIELD_SECTIONS,
  getBloodGroupLabel,
  toBaselinePayload,
} from "./baselineFormConfig";
import { BaselineFormFields } from "./BaselineFormFields.jsx";
import { useBaselineForm } from "./useBaselineForm";

const NOT_RECORDED = "-";

const BaselineDetails = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const patientId = useCurrentPatientId();
  const { data: baseline, refetch } = usePatientBaseline(patientId);
  const saveBaseline = useSavePatientBaseline();
  const form = useBaselineForm(baseline);

  const handleSave = (data) => {
    saveBaseline.mutate(
      {
        // No id: every save is a new history row (baselines are an
        // append-only timeline, never edited in place server-side).
        patientId: Number(patientId),
        ...toBaselinePayload(data),
      },
      {
        onSuccess: () => {
          toast.success("Baseline details saved successfully");
          setIsModalOpen(false);
          refetch();
        },
        onError: (error) => {
          console.error("Failed to save baseline details:", error);
          toast.error("Failed to save baseline details. Please try again.");
        },
      }
    );
  };

  const getDisplayValue = (field) => {
    if (!baseline) return NOT_RECORDED;
    if (field.name === "bloodGroup") return getBloodGroupLabel(baseline.bloodGroup);
    const value = baseline[field.name];
    return value || value === 0 ? value : NOT_RECORDED;
  };

  return (
    <>
      <CardWithTitle
        title="Baseline Details"
        titleAddon={
          !!baseline?.created && (
            <span className={s.recordedAt}>
              <Clock size={12} />
              Recorded {formatDate(baseline.created)}
            </span>
          )
        }
        endIcon={Pencil}
        buttonTitle="Add Baseline"
        className={s.editButton}
        onButtonClick={() => setIsModalOpen(true)}
      >
        {FIELD_SECTIONS.map((group) => {
          const SectionIcon = group.icon;

          return (
            <div key={group.section} className={s.section}>
              {/* With one section the heading only repeats the card's own title. */}
              {FIELD_SECTIONS.length > 1 && (
                <p className={s.sectionTitle}>
                  {!!SectionIcon && (
                    <SectionIcon size={14} className={s.sectionIcon} />
                  )}
                  {group.section}
                </p>
              )}
              <div className={s.tileGrid}>
                {group.fields.map((field) => {
                  const value = getDisplayValue(field);
                  const isNotRecorded = value === NOT_RECORDED;
                  const FieldIcon = field.icon;

                  return (
                    <div
                      key={field.name}
                      className={[s.tile, isNotRecorded && s.tileEmpty]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className={s.tileHeader}>
                        <p className={s.tileLabel}>{field.label}</p>
                        {!!FieldIcon && (
                          <span
                            className={[
                              s.tileIcon,
                              isNotRecorded && s.tileIconEmpty,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <FieldIcon size={16} strokeWidth={2} aria-hidden />
                          </span>
                        )}
                      </div>
                      <p
                        className={[
                          s.tileValue,
                          isNotRecorded && s.tileValueEmpty,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardWithTitle>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add Baseline"
        width={900}
      >
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave, onInvalidSubmit("BaselineDetails"))}
            className={s.formContainer}
          >
            <BaselineFormFields />
          </form>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={form.handleSubmit(handleSave, onInvalidSubmit("BaselineDetails"))}
            disabled={saveBaseline.isPending}
          >
            {saveBaseline.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export { BaselineDetails };
